import { RESPONSE_ACTION_TYPES } from '@/constants/config';

/**
 * Classify backend response actions / reasons so model-access gates
 * (buy-credits + go-plus / model_plus_only) are not mistaken for
 * "out of credits" depleted UX.
 */
export function classifyResponseError({
  actions,
  reason,
  errorCode,
  errorMessage,
} = {}) {
  const list = Array.isArray(actions) ? actions : [];
  const hasBuyCredits = list.some((a) => a?.action === RESPONSE_ACTION_TYPES.BUY_CREDITS || a?.action === 'buy-credits');
  const hasGetCredits = list.some((a) => a?.action === RESPONSE_ACTION_TYPES.GET_CREDITS || a?.action === 'get-credits');
  const hasGoPlus = list.some((a) => a?.action === RESPONSE_ACTION_TYPES.GO_PLUS || a?.action === 'go-plus');
  const reasonStr = String(reason || errorCode || '').toLowerCase();
  const msg = String(errorMessage || '').toLowerCase();

  // Explicit model gate, or dual CTAs that mean "unlock this model"
  // (not a zero-balance top-up).
  const isModelAccessError = (
    reasonStr === 'model_plus_only'
    || (reasonStr === 'plus_required' && hasBuyCredits && hasGoPlus)
    || (hasBuyCredits && hasGoPlus && reasonStr !== 'insufficient_credits' && !msg.includes('insufficient'))
  );

  const isCreditError = !isModelAccessError && (
    reasonStr === 'insufficient_credits'
    || hasGetCredits
    || (hasBuyCredits && !hasGoPlus)
    || msg.includes('insufficient')
  );

  const isPlusError = !isModelAccessError && !isCreditError && (
    reasonStr === 'plus_required'
    || hasGoPlus
  );

  return { isModelAccessError, isCreditError, isPlusError, hasBuyCredits, hasGoPlus };
}

/** Inline chat message helpers (MessageBubble / Slim / Wide). */
export function isModelAccessMessageError(message) {
  if (!message) return false;
  if (message.errorType === 'model_plus_only') return true;
  const buttons = message.buttons;
  if (!Array.isArray(buttons)) return false;
  const hasBuy = buttons.some((b) => b?.action === RESPONSE_ACTION_TYPES.BUY_CREDITS);
  const hasPlus = buttons.some((b) => b?.action === RESPONSE_ACTION_TYPES.GO_PLUS);
  return hasBuy && hasPlus;
}

export function isCreditMessageError(message) {
  if (!message) return false;
  if (isModelAccessMessageError(message)) return false;
  return message.errorType === 'INSUFFICIENT_CREDITS'
    || message?.buttons?.some((btn) => (
      btn?.action === RESPONSE_ACTION_TYPES.BUY_CREDITS
      || btn?.action === RESPONSE_ACTION_TYPES.GET_CREDITS
    ));
}

export function isPlusMessageError(message) {
  if (!message) return false;
  if (isModelAccessMessageError(message)) return true;
  if (isCreditMessageError(message)) return false;
  return message.errorType === 'PLUS_REQUIRED'
    || message.errorType === 'WORK_MODE_PLUS_REQUIRED'
    || message.errorType === 'model_plus_only'
    || message?.buttons?.some((btn) => btn?.action === RESPONSE_ACTION_TYPES.GO_PLUS);
}
